import React, { ForwardedRef, ChangeEvent, KeyboardEvent, forwardRef, memo, useCallback } from 'react';
import styled, { css } from 'styled-components';
import searchIcon from '@assets/icons/searchIcon.svg';
import arrowDown from '@assets/icons/arrowDown.svg';
import theme from '@styles/theme';

type StyledInputProps = {
	row?: boolean;
	asterisk?: boolean;
	arrow?: boolean;
	search?: boolean;
	button?: boolean;
	lengthCount?: boolean;
	width?: string | number;
	height?: number;
	padding?: string;
	margin?: string;
	border?: string;
	borderRadius?: number;
	title?: string;
	subTitle?: string;
	warning?: string;
	description?: string;
	name?: string;
	value: string;
	type?: string;
	onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
	onClickButton?: () => void;
	onKeyPress?: (e: KeyboardEvent<HTMLInputElement>) => void;
	maxLength?: number;
	disabled?: boolean;
	placeholder?: string;
};

const StyledInput = forwardRef<HTMLInputElement, StyledInputProps>(
	(
		{
			row,
			asterisk,
			arrow,
			search,
			button,
			lengthCount,
			width,
			height = 50,
			padding,
			margin,
			border,
			borderRadius = 4,
			title,
			subTitle,
			warning,
			description,
			name = '',
			value = '',
			type = 'text',
			onChange,
			onKeyPress,
			onClickButton,
			maxLength = 200,
			disabled = false,
			placeholder = ''
		},
		ref
	) => {
		const handleButtonClick = useCallback(() => {
			if (onClickButton) {
				onClickButton();
			}
		}, [onClickButton]);

		const widthValue = typeof width === 'number' ? `${width}px` : width;

		if (row) {
			return (
				<Wrapper $row={row} $width={widthValue} $margin={margin}>
					{title && (
						<RowTitle>
							{title}
							{asterisk && <Asterisk>*</Asterisk>}
						</RowTitle>
					)}
					<InputBox
						$arrow={arrow}
						$search={search}
						$width={widthValue}
						$height={height}
						$border={border}
						$borderRadius={borderRadius}
						$disabled={disabled}>
						<Input
							$padding={padding}
							$borderRadius={borderRadius}
							ref={ref}
							name={name}
							value={value}
							type={type}
							onChange={onChange}
							onKeyPress={onKeyPress}
							maxLength={maxLength}
							disabled={disabled}
							placeholder={placeholder}
							aria-label={title || placeholder || '입력'}
						/>
					</InputBox>
				</Wrapper>
			);
		}

		return (
			<Wrapper $row={row} $isDescription={!!description} $width={widthValue} $margin={margin}>
				{title && (
					<TitleBox>
						<ColumnTitle>
							{title}
							{subTitle && <SubTitle>{subTitle}</SubTitle>}
							{asterisk && <Asterisk>*</Asterisk>}
						</ColumnTitle>
						{lengthCount && (
							<LengthCount>
								({value.length}/{maxLength})
							</LengthCount>
						)}
					</TitleBox>
				)}
				<InputBox
					$arrow={arrow}
					$search={search}
					$width={widthValue}
					$height={height}
					$border={border}
					$borderRadius={borderRadius}
					$disabled={disabled}>
					<Input
						$padding={padding}
						$borderRadius={borderRadius}
						ref={ref}
						name={name}
						value={value}
						type={type}
						onChange={onChange}
						onKeyPress={onKeyPress}
						maxLength={maxLength}
						disabled={disabled}
						placeholder={placeholder}
						aria-label={title || placeholder || '입력'}
					/>
					{button && (
						<Button type="button" onClick={handleButtonClick}>
							입력
						</Button>
					)}
				</InputBox>
				{warning && (
					<WarningBox>
						<WarningText>{warning}</WarningText>
					</WarningBox>
				)}
				{description && (
					<DescriptionBox>
						<Description>{description}</Description>
					</DescriptionBox>
				)}
			</Wrapper>
		);
	}
);

StyledInput.displayName = 'StyledInput';

export default memo(StyledInput);

const Wrapper = styled.div<{ $row?: boolean; $isDescription?: boolean; $width?: string; $margin?: string }>`
	width: ${({ $width }) => $width ?? 'auto'};
	margin: ${({ $margin }) => $margin ?? 0};
	display: block;
	position: relative;

	${({ $row }) =>
		$row &&
		css`
			display: flex;
			align-items: center;
		`};

	${({ $isDescription }) =>
		$isDescription &&
		css`
			padding-bottom: 22px;
		`};
`;

const TitleBox = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 12px;
`;

const ColumnTitle = styled.div`
	font-size: 14px;
	display: flex;
	align-items: center;
`;

const RowTitle = styled.div`
	width: 180px;
	font-size: 14px;
`;

const SubTitle = styled.span`
	color: ${theme.colors.lightGrayFontColor};
	margin-left: 4px;
`;

const Asterisk = styled.span`
	color: ${theme.colors.activeRedOrange};
	margin-left: 2px;
`;

const InputBox = styled.div<{
	$search?: boolean;
	$arrow?: boolean;
	$width?: string;
	$height?: number;
	$border?: string;
	$disabled?: boolean;
	$borderRadius?: number;
}>`
	width: ${({ $width }) => $width ?? '100%'};
	height: ${({ $height }) => $height}px;
	border: ${({ $border }) => $border ?? `1px solid ${theme.colors.ultraLightGrayBorder}`};
	border-radius: ${({ $borderRadius }) => $borderRadius}px;
	display: flex;
	align-items: center;
	background-color: ${({ $disabled }) => ($disabled ? theme.colors.ultraLightGrayBorder : 'transparent')};
	transition: border-color 0.2s ease;

	&:focus-within {
		border-color: ${({ $disabled }) => ($disabled ? theme.colors.ultraLightGrayBorder : theme.colors.blackColor)};
	}

	${({ $search }) =>
		$search &&
		css`
			background-image: url(${searchIcon});
			background-size: 15px;
			background-repeat: no-repeat;
			background-position: top 50% right 12px;
		`};

	${({ $arrow }) =>
		$arrow &&
		css`
			background-image: url(${arrowDown});
			background-size: 22px;
			background-repeat: no-repeat;
			background-position: top 50% right 10px;
		`};
`;

const Input = styled.input<{
	$padding?: string;
	$borderRadius?: number;
}>`
	width: 100%;
	height: 100%;
	flex: 1;
	padding: ${({ $padding }) => $padding ?? '0 12px'};
	border: none;
	background: transparent;
	font-size: 14px;
	outline: none;
	border-radius: ${({ $borderRadius }) => $borderRadius}px;

	&::placeholder {
		color: ${theme.colors.deepGrayFontColor};
	}

	&:disabled {
		cursor: not-allowed;
		//color: ${theme.colors.deepGrayFontColor};
	}
`;

const WarningBox = styled.div`
	display: flex;
	align-items: center;
	margin: 8px 0 0 30px;
`;

const WarningText = styled.div`
	font-size: 14px;
	color: ${theme.colors.activeRedOrange};
`;

const DescriptionBox = styled.div`
	margin-top: 12px;
`;

const Description = styled.div`
	font-size: 14px;
	color: ${theme.colors.deepGrayFontColor};
	margin: 4px 0;
`;

const LengthCount = styled.div`
	${theme.typography.body2};
	color: ${theme.colors.deepGrayFontColor};
`;

const Button = styled.button`
	width: 42px;
	height: 22px;
	${theme.typography.caption};
	border: none;
	border-radius: 4px;
	background-color: ${theme.colors.grayBgColor};
	margin-right: 12px;
	cursor: pointer;
	transition: opacity 0.2s ease;

	&:hover {
		opacity: 0.8;
	}

	&:active {
		opacity: 0.6;
	}
`;
